import { C_TEAL, rgba } from '../utils/palette';
import { setupCanvas } from '../utils/canvas';
import { YEARS } from '../utils/constants';

export const CHART_KEY = [
    'Round reached each year: F=Final SF=Semi Q=Quali PQ=Pre-Quali · darker = deeper',
    '↑↗→↘↓ trend vs prior year · sorted by momentum score',
    'Recent season weighted most · only riders with last year entry · tap for detail',
];

const ROUND_DEPTH = {
    'Final': 4, 'Finals': 4,
    'Semifinal': 3, 'Semifinals': 3,
    'Qualifiers': 2, 'Qualification': 2, 'Qualifications': 2,
    'Pre-Qualifiers': 1,
};

const ROUND_SHORT = {
    'Final': 'F', 'Finals': 'F',
    'Semifinal': 'SF', 'Semifinals': 'SF',
    'Qualifiers': 'Q', 'Qualification': 'Q', 'Qualifications': 'Q',
    'Pre-Qualifiers': 'PQ',
};

const DEPTH_ALPHA = { 4: 0.75, 3: 0.45, 2: 0.22, 1: 0.1 };

function getBestEntry(rider, yr) {
    const entries = rider.entriesByYear?.[yr];
    return entries?.[0] ?? null;
}

function computeMomentum(rider) {
    const [y2, y1, y0] = YEARS;
    const e0 = getBestEntry(rider, y0);
    const e1 = getBestEntry(rider, y1);
    const e2 = getBestEntry(rider, y2);

    const d0 = e0 ? (ROUND_DEPTH[e0.round] ?? 0) : 0;
    const d1 = e1 ? (ROUND_DEPTH[e1.round] ?? 0) : 0;
    const d2 = e2 ? (ROUND_DEPTH[e2.round] ?? 0) : 0;

    const roundDelta = d0 - d1;
    const posDelta   = (e0 && e1 && e0.round === e1.round)
        ? (e1.position - e0.position)
        : 0;
    const consistency = (d0 > 0 ? 1 : 0) + (d1 > 0 ? 1 : 0) + (d2 > 0 ? 1 : 0);

    return d0 * 4 + roundDelta * 3 + posDelta * 0.5 + consistency;
}

function trendArrow(rider) {
    const [, y1, y0] = YEARS;
    const e0 = getBestEntry(rider, y0);
    const e1 = getBestEntry(rider, y1);
    const d0 = e0 ? (ROUND_DEPTH[e0.round] ?? 0) : 0;
    const d1 = e1 ? (ROUND_DEPTH[e1.round] ?? 0) : 0;
    if (!d0) return null;
    if (d0 > d1) return { arrow: '↑', color: '#4BA5A5' };
    if (d0 < d1) return { arrow: '↓', color: '#C4503A' };
    if (e0 && e1 && e0.position < e1.position) return { arrow: '↗', color: '#82c8a0' };
    if (e0 && e1 && e0.position > e1.position) return { arrow: '↘', color: '#d9896a' };
    return { arrow: '→', color: '#aaa' };
}

export function renderMomentumHeatmap(canvas, tooltip, riders) {
    if (!canvas || !riders.length) return;

    const [y2, y1, y0] = YEARS;
    const withMomentum = riders
        .map(r => ({ ...r, momentum: computeMomentum(r), trend: trendArrow(r) }))
        .filter(r => r.momentum > 0)
        .filter(r => getBestEntry(r, y1))
        .sort((a, b) => b.momentum - a.momentum)
        .slice(0, 20);

    if (!withMomentum.length) return;

    const w      = canvas.parentElement.clientWidth || 280;
    const rowH   = 20;
    const arrowW = 12;
    const nameW  = Math.floor(w * 0.32);
    const colW   = Math.floor((w - nameW - arrowW) / YEARS.length);
    const h      = withMomentum.length * rowH + 20;
    const ctx    = setupCanvas(canvas, w, h);
    ctx.clearRect(0, 0, w, h);

    // Header years
    YEARS.forEach((yr, yi) => {
        ctx.fillStyle   = '#bbb';
        ctx.font        = '8px monospace';
        ctx.textAlign   = 'center';
        ctx.textBaseline = 'alphabetic';
        ctx.fillText(yr, nameW + arrowW + yi * colW + colW / 2, 10);
    });

    const hitRows = [];
    withMomentum.forEach((rider, i) => {
        const y = 14 + i * rowH;

        // Rank label
        ctx.fillStyle    = '#ccc';
        ctx.font         = '8px monospace';
        ctx.textAlign    = 'right';
        ctx.textBaseline = 'middle';
        ctx.fillText(rider.position, 16, y + rowH / 2);

        // Name
        const maxNameW = nameW - 20;
        ctx.fillStyle  = '#666';
        ctx.font       = '8px monospace';
        ctx.textAlign  = 'left';
        let name = rider.rider;
        if (ctx.measureText(name).width > maxNameW) {
            while (ctx.measureText(name + '…').width > maxNameW && name.length > 1) name = name.slice(0, -1);
            name += '…';
        }
        ctx.fillText(name, 20, y + rowH / 2);

        // Trend arrow
        if (rider.trend) {
            ctx.fillStyle = rider.trend.color;
            ctx.font      = 'bold 12px monospace';
            ctx.textAlign = 'center';
            ctx.fillText(rider.trend.arrow, nameW + arrowW / 2, y + rowH / 2);
        }

        // Year cells
        YEARS.forEach((yr, yi) => {
            const entry = getBestEntry(rider, yr);
            const depth = entry ? (ROUND_DEPTH[entry.round] ?? 0) : 0;
            const cx    = nameW + arrowW + yi * colW;
            const alpha = depth ? DEPTH_ALPHA[depth] ?? 0.1 : 0.04;

            ctx.fillStyle = depth ? rgba(C_TEAL, alpha) : rgba('#888', 0.04);
            ctx.fillRect(cx + 1, y + 2, colW - 2, rowH - 4);

            if (entry) {
                ctx.fillStyle    = depth >= 3 ? '#3a8f8f' : '#888';
                ctx.font         = `${depth === 4 ? 'bold ' : ''}8px monospace`;
                ctx.textAlign    = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(
                    `${ROUND_SHORT[entry.round]} ${entry.position}`,
                    cx + colW / 2,
                    y + rowH / 2
                );
            }
        });

        hitRows.push({ y, rowH, rider });
    });

    // Tooltip
    if (canvas._momMM) canvas.removeEventListener('mousemove',  canvas._momMM);
    if (canvas._momML) canvas.removeEventListener('mouseleave', canvas._momML);
    canvas._momMM = (e) => {
        const rect = canvas.getBoundingClientRect();
        const my   = e.clientY - rect.top;
        const hit  = hitRows.find(r => my >= r.y && my < r.y + r.rowH);
        if (hit && tooltip) {
            const r    = hit.rider;
            const parts = YEARS.map(yr => {
                const entry = getBestEntry(r, yr);
                return entry ? `${yr}: ${ROUND_SHORT[entry.round]} ${entry.position}` : `${yr}: —`;
            });
            tooltip.textContent   = `${r.rider}  ·  ${parts.join('  ')}`;
            tooltip.style.display = 'block';
            const _mx = e.clientX - rect.left;
            const _tw = tooltip.offsetWidth;
            tooltip.style.left    = (_mx + 10 + _tw > canvas.parentElement.clientWidth ? _mx - _tw - 10 : _mx + 10) + 'px';
            tooltip.style.top     = (e.clientY - rect.top - 28) + 'px';
        } else if (tooltip) {
            tooltip.style.display = 'none';
        }
    };
    canvas._momML = () => { if (tooltip) tooltip.style.display = 'none'; };
    canvas.addEventListener('mousemove',  canvas._momMM);
    canvas.addEventListener('mouseleave', canvas._momML);
}
